import { Home, Grid3x3, CalendarCheck, User, Bell, Settings, HelpCircle, LogOut, ChevronRight } from 'lucide-react';
import { useGoogleFont, iconFor } from '@/components/website-templates/shared';

// Shared by WebsiteShellPanel's App view (interactive) and the Report page
// (read-only preview of "how the app looks") -- one source of truth for the
// phone-frame mockup instead of two copies drifting apart.

export const APP_SCREENS = [
  { id: 'home', label: 'Home', icon: Home },
  { id: 'services', label: 'Services', icon: Grid3x3 },
  { id: 'booking', label: 'Booking', icon: CalendarCheck },
  { id: 'profile', label: 'Profile', icon: User },
];

export const PhoneScreen = ({ screenId, brandKit, content }) => {
  const colors = brandKit?.colors || {};
  const bg = colors.background?.hex || '#FFFFFF';
  const fg = colors.foreground?.hex || '#1A1A1A';
  const primary = colors.primary?.hex || '#1B3A5C';
  const accent = colors.accent?.hex || primary;
  const headingFont = brandKit?.typography?.heading_font || 'Inter';
  const bodyFont = brandKit?.typography?.body_font || 'Inter';
  const logo = brandKit?.logos?.primary;
  const businessName = brandKit?.business_name || 'Your Business';
  const services = content?.services?.length ? content.services : [];

  useGoogleFont(headingFont);
  useGoogleFont(bodyFont);

  const wrapStyle = { background: bg, color: fg, fontFamily: `'${bodyFont}', sans-serif` };

  if (screenId === 'home') {
    return (
      <div style={wrapStyle} className="h-full flex flex-col">
        <div
          className="relative px-5 pt-4 pb-6 overflow-hidden"
          style={content?.hero_image_url ? { color: bg } : { background: primary, color: bg }}
        >
          {content?.hero_image_url && (
            <>
              <img src={content.hero_image_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${primary}99, ${primary}E6)` }} />
            </>
          )}
          <div className="relative">
            <div className="flex items-center gap-2 mb-5">
              {logo ? <img src={logo} alt="" className="w-6 h-6 object-contain" /> : <div className="w-6 h-6 rounded" style={{ background: bg }} />}
              <span style={{ fontFamily: `'${headingFont}', sans-serif` }} className="font-bold text-sm">{businessName}</span>
            </div>
            <p style={{ fontFamily: `'${headingFont}', sans-serif` }} className="text-xl font-bold leading-snug mb-1">
              {content?.headline || 'Welcome'}
            </p>
            <p className="text-xs opacity-85">{content?.subheadline}</p>
          </div>
        </div>
        <div className="flex-1 px-5 py-4 overflow-y-auto">
          <p className="text-xs font-semibold uppercase tracking-wide opacity-50 mb-2">Quick actions</p>
          <div className="grid grid-cols-2 gap-2.5 mb-5">
            {services.slice(0, 4).map((s, i) => {
              const Icon = iconFor(s.icon_hint);
              return (
                <div key={i} className="rounded-xl p-3" style={{ background: `${primary}0d`, border: `1px solid ${primary}22` }}>
                  <Icon className="w-4 h-4 mb-2" style={{ color: accent }} />
                  <p className="text-xs font-semibold leading-tight">{s.title}</p>
                </div>
              );
            })}
          </div>
          <div className="rounded-xl p-4" style={{ background: `${accent}12` }}>
            <p className="text-xs leading-relaxed opacity-80">&ldquo;{content?.testimonial_quote}&rdquo;</p>
            <p className="text-[10px] opacity-50 mt-1.5 font-semibold">{content?.testimonial_author}</p>
          </div>
        </div>
      </div>
    );
  }

  if (screenId === 'services') {
    return (
      <div style={wrapStyle} className="h-full flex flex-col">
        <div className="px-5 pt-5 pb-3">
          <p style={{ fontFamily: `'${headingFont}', sans-serif` }} className="text-lg font-bold">Services</p>
        </div>
        <div className="flex-1 px-5 space-y-2.5 overflow-y-auto pb-4">
          {services.map((s, i) => {
            const Icon = iconFor(s.icon_hint);
            return (
              <div key={i} className="flex items-center gap-3 rounded-xl p-3" style={{ border: `1px solid ${fg}14` }}>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: accent }}>
                  <Icon className="w-4 h-4" style={{ color: bg }} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold truncate">{s.title}</p>
                  <p className="text-[11px] opacity-60 truncate">{s.description}</p>
                </div>
                <ChevronRight className="w-3.5 h-3.5 opacity-30 ml-auto shrink-0" />
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (screenId === 'booking') {
    return (
      <div style={wrapStyle} className="h-full flex flex-col">
        <div className="px-5 pt-5 pb-3">
          <p style={{ fontFamily: `'${headingFont}', sans-serif` }} className="text-lg font-bold">{content?.cta_headline || 'Book a spot'}</p>
          <p className="text-xs opacity-60 mt-1">{content?.cta_body}</p>
        </div>
        <div className="px-5 space-y-3 mt-3">
          {['Name', 'Email', 'Preferred date'].map((label) => (
            <div key={label}>
              <p className="text-[10px] font-semibold uppercase tracking-wide opacity-50 mb-1">{label}</p>
              <div className="h-9 rounded-lg" style={{ border: `1px solid ${fg}22` }} />
            </div>
          ))}
          <button className="w-full py-3 rounded-lg text-sm font-semibold mt-2" style={{ background: primary, color: bg }}>
            {content?.cta_button_text || 'Book Now'}
          </button>
        </div>
      </div>
    );
  }

  // profile
  const items = [
    { icon: Bell, label: 'Notifications' },
    { icon: Settings, label: 'Settings' },
    { icon: HelpCircle, label: 'Help & Support' },
    { icon: LogOut, label: 'Log Out' },
  ];
  return (
    <div style={wrapStyle} className="h-full flex flex-col">
      <div className="px-5 pt-6 pb-5 flex flex-col items-center" style={{ background: `${primary}0d` }}>
        <div className="w-14 h-14 rounded-full flex items-center justify-center mb-2 text-lg font-bold" style={{ background: primary, color: bg }}>
          {businessName.slice(0, 1).toUpperCase()}
        </div>
        <p className="text-sm font-semibold">Client Account</p>
        <p className="text-[11px] opacity-50">via {businessName}</p>
      </div>
      <div className="flex-1 px-5 pt-4 space-y-1">
        {items.map(({ icon: Icon, label }) => (
          <div key={label} className="flex items-center gap-3 py-2.5">
            <Icon className="w-4 h-4 opacity-60" />
            <span className="text-xs flex-1">{label}</span>
            <ChevronRight className="w-3.5 h-3.5 opacity-30" />
          </div>
        ))}
      </div>
    </div>
  );
};

// width in px; height scales at the same 2:1 ratio as the original 300x600 frame.
export const PhoneFrame = ({ screenId, onScreenChange, brandKit, content, width = 300, primaryColor }) => {
  const height = width * 2;
  const primary = primaryColor || brandKit?.colors?.primary?.hex || '#1B3A5C';
  const interactive = typeof onScreenChange === 'function';

  return (
    <div
      className="rounded-[2.5rem] p-3 shadow-xl"
      style={{ background: '#111318', width: `${width}px` }}
      data-testid="app-phone-frame"
    >
      <div className="rounded-[2rem] overflow-hidden bg-white" style={{ height: `${height}px` }}>
        <div className="h-6 flex items-center justify-center" style={{ background: '#111318' }}>
          <div className="w-16 h-1 rounded-full bg-white/20" />
        </div>
        <div style={{ height: `calc(${height}px - 24px - 56px)` }}>
          <PhoneScreen screenId={screenId} brandKit={brandKit} content={content} />
        </div>
        <div className="h-14 flex items-center justify-around border-t" style={{ borderColor: '#0000000f' }}>
          {APP_SCREENS.map((s) => {
            const Icon = s.icon;
            const active = screenId === s.id;
            return (
              <button
                key={s.id}
                onClick={() => interactive && onScreenChange(s.id)}
                className="flex flex-col items-center gap-0.5"
                disabled={!interactive}
              >
                <Icon className="w-4 h-4" style={{ color: active ? primary : '#00000040' }} />
                <span className="text-[9px]" style={{ color: active ? primary : '#00000040' }}>{s.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
